import { Checkbox, Popover } from 'antd';
import React, { FC } from 'react';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import Form from 'antd/es/form';
import { useContextReports } from '../../../../context/reports';
import { calculateDownPaymentPrice } from '../../../../utils/calculateDownPaymentPrice';
import { EnumClosingCostType } from '../../../../types/settings';
import { calculateLoanClosingCostAmount } from '../../../../utils/calculateLoanClosingCostAmount';
import { formatAmount } from '../../../../utils/formatAmount';
import { calculateAPR } from '../../../../utils/calculateAPR';
import { calculatePrincipalInterest } from '../../../../utils/calculatePrincipalInterest';

type Props = {
  loanIndex: number;
}

const RemoveClosingCostsCol: FC<Props> = ({ loanIndex }) => {
  const form = Form.useFormInstance();
  const {
    handleLoansUpdate,
    reportClosingCosts,
    reportLoanClosingCosts
  } = useContextReports();

  const loan = form.getFieldValue(['loans', loanIndex]);
  const closingCostsClearedAt = form.getFieldValue(['loans', loanIndex, 'externalLoan', 'closingCostsClearedAt']);
  const interestRate = parseFloat(form.getFieldValue(['loans', loanIndex, 'interestRate', 'value'])) || 0;
  const loanAmount = parseFloat(form.getFieldValue(['loans', loanIndex, 'loanAmount', 'value'])) || 0;
  const loanPeriodInYears = parseFloat(form.getFieldValue(['loans', loanIndex, 'loanPeriodInYears', 'value'])) || 0;

  const uipActive = form.getFieldValue(['tableView', 'uip', 'active']);
  const vaFundingFeeActive = form.getFieldValue(['tableView', 'vaFundingFee', 'active']);
  const loanPointsActive = form.getFieldValue(['tableView', 'loanPoints', 'active']);

  const onCheckboxChange = (event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    const value = checked ? new Date() : null;

    form.setFieldValue(['loans', loanIndex, 'externalLoan', 'closingCostsClearedAt'], value)

    const downPaymentPrice = calculateDownPaymentPrice(form, loanIndex)

    form.setFieldValue(['loans', loanIndex, 'down_payment_price'], downPaymentPrice)

    const loanClosingCost = reportLoanClosingCosts?.filter(cost => cost.loanId === loan?.id);

    const totalAprRelatedClosingCost = loanClosingCost?.reduce((sum, closingCost) => {
      const reportClosingCost = reportClosingCosts?.find(c => c.name === closingCost.name);

      if (closingCost.name === 'uip' && uipActive) {
        return sum + closingCost.amount
      }

      if (closingCost.name === 'vaFundingFee' && vaFundingFeeActive) {
        return sum + closingCost.amount
      }

      if (closingCost.name === 'loan_points' && loanPointsActive) {
        return sum + closingCost.amount
      }

      if (reportClosingCost?.aprIncluded && reportClosingCost?.active && !checked) {
        const closingCostAmount = closingCost.type === EnumClosingCostType.flatFee ?
          closingCost.amount : reportClosingCost?.amount ?? 0;

        const amount = calculateLoanClosingCostAmount(
          loan, closingCost.type,
          closingCostAmount,
          closingCost.propertyTaxesMonthsCollected,
          closingCost.daysCollected
        )

        return sum + amount;
      }

      return sum
    }, 0) ?? 0

    const normalizedAprClosingCost = parseFloat(formatAmount(Math.max(0, totalAprRelatedClosingCost)));

    const apr = calculateAPR(loanAmount, interestRate, loanPeriodInYears, normalizedAprClosingCost);

    form.setFieldValue(['loans', loanIndex, 'apr', 'value'], apr);
    form.setFieldValue(['loans', loanIndex, 'aprRelatedClosingCost', 'value'], normalizedAprClosingCost);

    const principalInterest = calculatePrincipalInterest(loan);

    form.setFieldValue(['loans', loanIndex, 'principalInterest', 'value'], principalInterest);

    handleLoansUpdate?.({})
  }

  return (
    <div style={{ marginTop: 'auto', marginLeft: 'auto' }}>
      <Popover content="Remove closing costs in this column">
        <Checkbox
          checked={Boolean(closingCostsClearedAt)}
          onChange={onCheckboxChange}
        />
      </Popover>
    </div>
  )
}

export default RemoveClosingCostsCol;
